import { ConciliacaoBancariaResponse } from "core/features/conciliacao-bancaria/typings";
import { DadosExtratoBancarioListResponse } from "features/conciliacao-bancaria/typings";
import { getDiasMes } from "infra/helpers/getDiasMes";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import xlsx, { IContent, IJsonSheet, ISettings } from "json-as-xlsx";
import moment from "moment";

type DiaAConciliar = {
  dia: number;
  numeroContas: number;
  valorDespesas: number;
  valorReceitas: number;
};

export function exportExcelConciliacaoBancaria({
  mes,
  conciliacao,
}: {
  mes: string;
  conciliacao: ConciliacaoBancariaResponse;
}) {
  const settings: ISettings = {
    fileName: `Conciliação Bancária - ${moment(mes, "YYYY-MM").format(
      "MM_YYYY"
    )}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsDia = getColumnsDia();
  const columnsAConciliar = getColumnsAConciliar();

  const diasMes = getDiasMes(mes);

  const diasAConciliar: DiaAConciliar[] = [];

  const sheetsData: IJsonSheet[] = [];

  diasMes.forEach((dia) => {
    const dadosDia = conciliacao.dias.find((d) => dia === d.numeroDia);
    const _conciliado =
      dadosDia?.conciliado && dadosDia?.totalContasSemExtrato === 0;

    const sheetName = `Dia ${dia} (${
      dadosDia?.fimDeSemana
        ? "Fim de Semana"
        : _conciliado
        ? "Conciliado"
        : dadosDia?.conciliado === false || !!dadosDia?.totalContasSemExtrato
        ? "Não conciliado"
        : "Sem lançamento"
    })`;

    const item = conciliacao.items.find(
      (item) => moment(item.data).date() === dia
    );
    const contasDia = item?.dados;

    const numeroContasAConciliar = item?.totalContasSemExtrato;
    const valorDespesaAConciliar = item?.totalLiquidoDespesas;
    const valorReceitaAConciliar = item?.totalLiquidoReceitas;

    if (numeroContasAConciliar) {
      diasAConciliar.push({
        dia,
        numeroContas: numeroContasAConciliar,
        valorDespesas: valorDespesaAConciliar ?? 0,
        valorReceitas: valorReceitaAConciliar ?? 0,
      });
    }

    sheetsData.push({
      sheet: sheetName,
      columns: columnsDia,
      content: contasDia?.map(defaultContentMapperXlsx) ?? [],
    });
  });

  if (diasAConciliar.length) {
    sheetsData.unshift({
      sheet: "Dias com contas a conciliar",
      columns: columnsAConciliar,
      content: diasAConciliar?.map(defaultContentMapperXlsx) ?? [],
    });
  }

  xlsx(sheetsData, settings);
}

function getColumnsAConciliar() {
  return [
    {
      label: "Dia",
      value: (row: IContent) => {
        const _row = row as unknown as DiaAConciliar;
        return `${_row.dia}`;
      },
    },
    {
      label: "Contas a conciliar",
      format: "#,###0.00",
      value: (row: IContent) => {
        const _row = row as unknown as DiaAConciliar;
        return `${_row.numeroContas}`;
      },
    },
    {
      label: "Valor de Receitas",
      format: "#,###0.00",
      value: (row: IContent) => {
        const _row = row as unknown as DiaAConciliar;
        return `${_row.valorReceitas}`;
      },
    },
    {
      label: "Valor de Despesas",
      format: "#,###0.00",
      value: (row: IContent) => {
        const _row = row as unknown as DiaAConciliar;
        return `${_row.valorDespesas}`;
      },
    },
  ];
}

function getColumnsDia() {
  return [
    {
      label: "Extrato Bancário",
      value: (row: IContent) => {
        const _row = row as unknown as DadosExtratoBancarioListResponse;
        return `${_row.descricaoExtratoBancario}\n${_row.subDescricaoExtrato}`;
      },
    },
    {
      label: "Valor do Extrato",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as DadosExtratoBancarioListResponse)?.valor ?? "",
    },
    {
      label: "No Sistema",
      value: (row: IContent) => {
        const _row = row as unknown as DadosExtratoBancarioListResponse;
        const contas = _row.contas;

        return `${
          contas.length === 1
            ? contas[0].descricao
            : contas.length > 1
            ? `${contas.length} cobranças`
            : null
        }`;
      },
    },
    {
      label: "Conciliado",
      value: (row: IContent) => {
        const _row = row as unknown as DadosExtratoBancarioListResponse;

        return _row.conciliado ? "Sim" : "Não";
      },
    },
  ];
}
